import type { CSSProperties } from "react";

// Preview protection person reveal — the pure half of a port of photohawk-app's RevealKit
// (src/views/BrowseGallery/components/RevealKit.jsx), so the admin shows protected photos exactly as
// buyers see them. Keep in step with the app: same constants, geometry and layer protocol.
//
// A protected hit's `protect.regions` are its revealable people as percent boxes of the photo (largest
// first; index = spot). Each person has three server renders, all the same size, window and watermark
// layout (protect v3, `protect.reveal === 'layers'`):
//   layer=frame   the reveal window (`win`, or `winTall` for shape=tall), the person still obscured
//   layer=top     identical to frame, except the person's rows `halves.top` are clear
//   layer=bottom  the same for rows `halves.bottom`
// The viewer shows `frame` with one half stacked on top, CSS-masked to a band of the person
// (`bandHeight` of their height) centred at `c`. Moving the band only changes the mask.

export interface RevealBox {
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface RevealRegion extends RevealBox {
  win?: RevealBox | null;
  winTall?: RevealBox | null;
}
export interface RevealProtect {
  mode?: string;
  ready?: boolean;
  reveal?: string;
  bandHeight?: number;
  halves?: { top: [number, number]; bottom: [number, number] };
  regions?: RevealRegion[];
}
export type RevealLayer = "frame" | "top" | "bottom";
export type RevealShape = "tall" | null;
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** No long-press save / selection on reveal images and controls. */
export const noSave: CSSProperties = { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" };

export const MAX_REVEAL_MS = 8000;
// Keyboard step for the band centre (fraction of the person's height).
export const KEY_STEP = 0.05;
// One full auto-sweep, head → feet → head.
export const SWEEP_CYCLE_MS = 7000;
// A press on a person that moves further than this is a slide (takes over the band), not a tap.
export const SLIDE_PX = 6;
// Person affordance icons: diameter on touch / mouse devices, the smallest worth drawing, and the
// shortest on-screen person that gets one.
export const HINT_SIZE_TOUCH = 36;
export const HINT_SIZE_MOUSE = 30;
const HINT_SIZE_MIN = 20;
const HINT_MIN_PERSON_PX = 36;
// The clear head band (fraction of the person's box from the top): the icon never overlaps it.
const HEAD_BAND = 0.25;
export const MAX_HINTS = 12;
// Mask feather, % of the reveal window's height (straddles each band edge).
const FEATHER_PCT = 1.5;
export const LAYERS: RevealLayer[] = ["frame", "top", "bottom"];
// Phone-width screens (the viewer fills the height and uses tall reveals).
export const MOBILE_MAX_WIDTH = 768;

export const spotUrl = (src: string, index: number, layer: RevealLayer, shape?: RevealShape) =>
  `${src}${src.includes("?") ? "&" : "?"}spot=${index}&layer=${layer}${shape === "tall" ? "&shape=tall" : ""}`;

/** The revealable people of a hit — only the layered protocol is understood. */
export const revealRegions = (protect?: RevealProtect | null): RevealRegion[] =>
  protect && protect.ready && protect.reveal === "layers" && Array.isArray(protect.regions) ? protect.regions : [];

export const revealBandHeight = (protect?: RevealProtect | null) => {
  const bh = protect ? Number(protect.bandHeight) : NaN;
  return bh > 0 && bh < 1 ? bh : 0.25;
};

const defaultHalves = (bh: number) => ({ top: [0, 0.5 + bh / 2] as [number, number], bottom: [0.5 - bh / 2, 1] as [number, number] });
export const revealHalves = (protect?: RevealProtect | null) => {
  const h = protect?.halves;
  const ok = (r: unknown): r is [number, number] =>
    Array.isArray(r) && r.length === 2 && Number.isFinite(r[0]) && Number.isFinite(r[1]);
  return h && ok(h.top) && ok(h.bottom) ? h : defaultHalves(revealBandHeight(protect));
};

/** shape=tall only when every person has a tall window (winTall is null when the server doesn't know the size). */
export const tallOk = (regions: RevealRegion[]) => regions.length > 0 && regions.every((r) => r && r.winTall);

export const isPhone = () => typeof window !== "undefined" && window.innerWidth <= MOBILE_MAX_WIDTH;

/** Band centre, clamped so the whole band stays on the person. */
export const clampC = (c: number, bh: number) => Math.max(bh / 2, Math.min(1 - bh / 2, Number.isFinite(c) ? c : bh / 2));

/** The band centre for a pointer at clientY, over a person spanning [top, top + height) on screen. */
export const bandAt = (clientY: number, top: number, height: number, bh: number) =>
  clampC(height > 0 ? (clientY - top) / height : 0, bh);

/** Which half image holds band c (each half contains every band on its side). */
export const halfFor = (c: number): "top" | "bottom" => (c <= 0.5 ? "top" : "bottom");

/**
 * CSS mask limiting a half image to the band rows, clipped to the half's own rows. Person fractions →
 * window percent: yWin = (region.y + t·region.height − win.y) / win.height · 100.
 */
export const bandMask = (
  region: RevealBox,
  win: RevealBox,
  c: number,
  bh: number,
  halves: { top: [number, number]; bottom: [number, number] },
  feather = FEATHER_PCT,
) => {
  const half = halves[halfFor(c)];
  const t0 = Math.max(c - bh / 2, half[0]);
  const t1 = Math.min(c + bh / 2, half[1]);
  const y = (t: number) => ((region.y + t * region.height - win.y) / win.height) * 100;
  const f = feather / 2;
  const n = (v: number) => `${Math.round(v * 100) / 100}%`;
  const a = y(t0);
  const b = y(t1);
  return `linear-gradient(to bottom, transparent ${n(a - f)}, #000 ${n(a + f)}, #000 ${n(b - f)}, transparent ${n(b + f)})`;
};

/** scanning: 'slider' (the viewer) or false (lightbox / example, where a click opens the viewer). */
export const defaultHint = (touch: boolean, revealed: boolean, scanning: "slider" | false) => {
  if (scanning === "slider" && revealed) {
    return touch ? "Drag the slider to scan · tap the photo to go back" : "Drag the slider to scan · click the photo to go back";
  }
  return touch ? "Tap a person to see them clearly" : "Click a person to see them clearly";
};

/** Fit a w×h image into a W×H box (contain or cover), centred. */
export const fitRect = (W: number, H: number, w: number, h: number, fit: "contain" | "cover"): Rect => {
  const s = fit === "cover" ? Math.max(W / w, H / h) : Math.min(W / w, H / h);
  return { left: (W - w * s) / 2, top: (H - h * s) / 2, width: w * s, height: h * s };
};

// ---- preload: fetch + decode reveal renders before they're needed ---------------------------------
// One entry per URL for the page's life: a decoded Image (and its size). Listeners are told when a URL
// becomes ready.
interface CacheEntry {
  ready: boolean;
  failed: boolean;
  promise: Promise<boolean> | null;
  width: number;
  height: number;
}
const cache = new Map<string, CacheEntry>();
const listeners = new Set<(url: string) => void>();

export const warm = (url?: string | null) => {
  if (!url || typeof Image === "undefined") return null;
  const existing = cache.get(url);
  if (existing) return existing.promise;
  const entry: CacheEntry = { ready: false, failed: false, promise: null, width: 0, height: 0 };
  cache.set(url, entry);
  const img = new Image();
  img.decoding = "async";
  entry.promise = new Promise<boolean>((resolve) => {
    const done = (ok: boolean) => {
      entry.ready = ok;
      entry.failed = !ok;
      entry.width = img.naturalWidth;
      entry.height = img.naturalHeight;
      if (!ok) cache.delete(url); // let a later attempt retry
      listeners.forEach((fn) => fn(url));
      resolve(ok);
    };
    img.onload = () => {
      if (img.decode) img.decode().then(() => done(true), () => done(true));
      else done(true);
    };
    img.onerror = () => done(false);
  });
  img.src = url;
  return entry.promise;
};
export const isReady = (url?: string | null) => !!(url && cache.get(url)?.ready);
export const sizeOf = (url?: string | null) => {
  const e = url ? cache.get(url) : undefined;
  return e && e.ready && e.width && e.height ? { width: e.width, height: e.height } : null;
};
export const onLayerReady = (fn: (url: string) => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};
/** Tests only. */
export const resetRevealCacheForTests = () => {
  cache.clear();
  gridPulseClaimed = false;
};

// ---- person affordance -----------------------------------------------------------------------------
const SEEN_KEY = "phx-reveal-seen";
export const hasRevealedBefore = () => {
  try {
    return typeof window !== "undefined" && window.sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
};
export const markRevealed = () => {
  try { window.sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* storage blocked: keep pulsing */ }
};

/**
 * Where person r's icon goes, in the px space of `rect` (the photo's on-screen box): centred across the
 * person, ~45% down their box — below the clear head band — and inside the box. Null for a person too
 * small to tap comfortably.
 */
export const hintSpot = (r: RevealBox, rect: Rect, base: number) => {
  const w = (r.width / 100) * rect.width;
  const h = (r.height / 100) * rect.height;
  if (h < HINT_MIN_PERSON_PX) return null;
  const size = Math.round(Math.max(HINT_SIZE_MIN, Math.min(base, (1 - HEAD_BAND) * h * 0.5, w * 0.9)));
  if (size > w || size > (1 - HEAD_BAND) * h) return null;
  const left = rect.left + (r.x / 100) * rect.width;
  const top = rect.top + (r.y / 100) * rect.height;
  const cy = Math.min(h - size / 2, Math.max(0.45 * h, HEAD_BAND * h + size / 2));
  return { x: left + w / 2, y: top + cy, size };
};

// ---- grid thumbnails --------------------------------------------------------------------------------
// An icon on the body of each of the largest people (regions are largest first; at most 3, any size),
// kept inside the visible tile; a click on a person (icon or box) opens the viewer on them.
export const GRID_MAX_PEOPLE = 3;
const GRID_HINT_TOUCH = 28;
const GRID_HINT_MOUSE = 24;
const GRID_HINT_MIN = 18;
const GRID_PULSE_KEY = "phx-grid-pulsed";
let gridPulseClaimed = false;
/** One tile pulses — the first with person icons, once per session, and not after a reveal. */
export const claimGridPulse = () => {
  if (gridPulseClaimed) return false;
  gridPulseClaimed = true;
  if (hasRevealedBefore()) return false;
  try {
    if (window.sessionStorage.getItem(GRID_PULSE_KEY) === "1") return false;
    window.sessionStorage.setItem(GRID_PULSE_KEY, "1");
  } catch { /* storage blocked: still only this tile, this page */ }
  return true;
};

/**
 * Where a grid icon goes (px, in the tile): centred across the person, ~45% down with its top below the
 * clear head band, then clamped inside the visible tile (vw × vh).
 */
export const gridSpot = (r: RevealBox, rect: Rect, base: number, vw: number, vh: number) => {
  const w = (r.width / 100) * rect.width;
  const h = (r.height / 100) * rect.height;
  const size = Math.round(Math.max(GRID_HINT_MIN, Math.min(base, (1 - HEAD_BAND) * h * 0.5, w * 0.9)));
  const left = rect.left + (r.x / 100) * rect.width;
  const top = rect.top + (r.y / 100) * rect.height;
  let cy = Math.max(0.45 * h, HEAD_BAND * h + size / 2);
  if (cy > h - size / 2) cy = Math.max(HEAD_BAND * h + size / 2, h - size / 2);
  const half = size / 2;
  const clamp = (v: number, hi: number) => Math.max(half, Math.min(Math.max(half, hi - half), v));
  return { x: clamp(left + w / 2, vw), y: clamp(top + cy, vh), size };
};

/**
 * What a thumbnail shows: null or { people: [{ box, spot }] } for the largest GRID_MAX_PEOPLE people
 * (index i = region i). rect = the image's box in the tile (px; an object-cover image may overflow it),
 * vw × vh = the visible tile.
 */
export const gridPlan = (regions: RevealRegion[], rect: Rect | null, vw: number, vh: number, touch: boolean) => {
  if (!regions.length || !rect || !(rect.width > 0) || !(rect.height > 0)) return null;
  const base = touch ? GRID_HINT_TOUCH : GRID_HINT_MOUSE;
  return {
    people: regions.slice(0, GRID_MAX_PEOPLE).map((r) => ({
      spot: gridSpot(r, rect, base, vw > 0 ? vw : rect.width, vh > 0 ? vh : rect.top + rect.height),
      box: {
        left: rect.left + (r.x / 100) * rect.width,
        top: rect.top + (r.y / 100) * rect.height,
        width: (r.width / 100) * rect.width,
        height: (r.height / 100) * rect.height,
      },
    })),
  };
};
